'use client'

import { useCallback, useState, type MouseEvent } from 'react'
import { useRouter } from 'next/navigation'
import { usePrescriptionRenewalDestination } from '@/hooks/usePrescriptionRenewalDestination'

function isUnmodifiedPrimaryClick(event?: MouseEvent) {
    if (!event) return true
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

/**
 * Storefront click handling for prescription renewal links.
 *
 * Logged-out visitors keep the public 200007 href and native navigation.
 * Logged-in patients use the resolved destination href on the link once it is
 * known (including Cmd/Ctrl-click). While that destination is still loading,
 * an unmodified click waits instead of following the public storefront href.
 */
export function usePrescriptionRenewalStorefrontNavigation(loggedIn: boolean) {
    const router = useRouter()
    const { href, isLoading, waitForHref } = usePrescriptionRenewalDestination({ skip: !loggedIn })
    const [isClickPending, setIsClickPending] = useState(false)

    const navigateToPrescriptionRenewal = useCallback(
        async (event?: MouseEvent) => {
            if (!loggedIn || !isLoading || !isUnmodifiedPrimaryClick(event)) return

            event?.preventDefault()
            setIsClickPending(true)
            try {
                router.push(await waitForHref())
            } finally {
                setIsClickPending(false)
            }
        },
        [loggedIn, isLoading, router, waitForHref],
    )

    return {
        href,
        isNavigating: loggedIn && (isLoading || isClickPending),
        navigateToPrescriptionRenewal,
    }
}
